import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function HongKongButlerAiProfitabilityPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Hong Kong Butler AI Achieves Profitability' }
  ];

  const article = {
    category: 'Technology & Business Success Stories',
    date: 'February 1, 2025',
    title: 'Hong Kong Butler AI Achieves Profitability',
    subtitle: 'AI Platform Achieves Triple Revenue Growth',
    heroImage: '/images/generated/jba-fs-089-news_hong-kong-butler-ai-profitability.webp',
    lead: 'Hong Kong-based startup "Butler" transformed from a housekeeping service to an AI-based property management platform, achieving profitability in Q4 2024. With revenue growing three times year-over-year, this is a notable case of the "Butler" concept succeeding as a technology business.',
    sections: [
      {
        heading: 'Company Overview and Business Model Transformation',
        image: '/images/generated/jba-fs-090-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-[4/3]',
        alt: 'An AI platform riding the wave of large language model advances',
        paragraphs: [
          'Hong Kong-based startup "Butler" initially started as a housekeeping service but strategically pivoted to an AI-based property management platform. This transformation allowed the company to ride the wave of enterprises leveraging rapid advances in large language models.',
          'According to co-founder Angues Chan, 2024 revenue grew three times year-over-year, achieving profitability in Q4 2024. Furthermore, the number of properties served is expected to triple in 2025.'
        ]
      },
      {
        heading: 'AI Platform Service Content',
        image: '/images/generated/jba-fs-091-news_hong-kong-butler-ai-profitability-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'A property management service automating operational workflows',
        paragraphs: [
          'The AI platform provides comprehensive solutions to help property managers and landlords oversee workflows. Specifically, it handles tasks such as managing tenant and resident inquiries, processing updates from maintenance workers, and automating property operations workflows.',
          'It was used in over 65 residential complexes in 2024, significantly expanding from fewer than 10 properties when the platform launched in 2022. This growth demonstrates efficiency improvements and customer satisfaction through AI technology.'
        ]
      },
      {
        heading: 'Hong Kong Real Estate Market Background and Technological Innovation',
        image: '/images/generated/jba-fs-092-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-[3/4]',
        alt: "Hong Kong's real estate market in a prolonged downturn",
        paragraphs: [
          "Hong Kong's real estate market is in a prolonged downturn with housing prices continuously declining. In this challenging market environment, AI-enabled solutions are significantly contributing to cost reduction for property managers.",
          'Founder Angues Chan comments, "Hong Kong\'s real estate industry has been in a privileged position for a long time. Now things are finally not going well, and they must change," emphasizing the need for industry transformation.'
        ]
      },
      {
        heading: 'Technological Realization of the "Butler" Concept',
        image: '/images/generated/jba-fs-093-news_hong-kong-butler-ai-profitability-v3.webp', aspect: 'aspect-[4/3]',
        alt: 'A digital butler reproducing meticulous care at scale',
        paragraphs: [
          'This success story shows that traditional industries can transform business models using AI technology and grow even under harsh market conditions. This platform named "Butler" technologically realizes the butler service concept—personalized efficient management and problem-solving.',
          'It is noted as a groundbreaking case of creating new value by deploying the meticulous care and problem-solving abilities traditionally provided by butlers as a scalable business model through AI technology.'
        ]
      }
    ],
    comment: 'The success of Hong Kong Butler AI is deeply noted by our association as a groundbreaking case proving that the "butler" concept can be realized as a technology business. The deployment of personalized management and problem-solving abilities—the essence of butler services—as a scalable solution through AI technology contains important suggestions about the complementary relationship between traditional butler services and technology. While human butler services provide warm service and AI platform efficiency each have different values, they both demonstrate the expandability of the "butler" concept. Such evolution of the butler concept in the technology age holds potential for creating new service forms in Japan\'s property management market and various industries, and we will actively pursue research toward establishing next-generation butler services where humans and technology collaborate.',
    source: [
      { label: 'Source', value: 'South China Morning Post', link: false },
      { label: 'Article URL', value: 'https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-en-014-news_hong-kong-butler-ai-profitabili-v2.webp', aspect: 'aspect-[4/3]', alt: 'Related article: AI Hospitality Revolution: 5 Transformations', title: 'AI Hospitality Revolution: 5 Transformations', desc: 'The future of butler services opened by AI' },
      { to: '/en/news/apple-robot-butler', image: '/images/generated/jba-fs-en-015-news_hong-kong-butler-ai-profitabili.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Apple Robot Butler Development Project', title: 'Apple Robot Butler Development Project', desc: "Butler robot development by world's largest tech company" }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'South China Morning Post', link: false },
    { label: 'Original Article', value: 'https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-overview', label: 'Company Overview and Business Model Transformation' },
    { id: 's-numbers', label: 'Growth by the Numbers' },
    { id: 's-service', label: 'AI Platform Service Content' },
    { id: 's-market', label: 'Hong Kong Real Estate Market Background' },
    { id: 's-pivot', label: 'The Pivot Born of a Property Downturn' },
    { id: 's-concept', label: 'Technological Realization of the "Butler" Concept' }
  ];

  return (
    <Layout>
      <title>Hong Kong Butler AI Achieves Profitability | Japan Butler Association</title>
      <meta name="description" content="Hong Kong startup Butler pivots from housekeeping to an AI-based property management platform, achieving profitability in Q4 2024 with revenue tripling year-over-year." />
      <link rel="canonical" href="https://butler-association.jp/en/news/hong-kong-butler-ai-profitability" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/hong-kong-butler-ai-profitability" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/hong-kong-butler-ai-profitability" />

      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Hong Kong startup "Butler" pivoted from housekeeping to AI property management, reaching profitability in Q4 2024',
                '2024 revenue tripled year-over-year across 65+ residential complexes, expected to triple again in 2025',
                'A case of realizing the "butler" concept—personalized management and problem-solving—at scale through AI'
              ]}
            />

            <SectionHeading id="s-overview" en="Company">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-numbers" en="By the Numbers">Growth by the Numbers</SectionHeading>
            <div className="space-y-6">
              <p className={P}>According to SCMP, Butler's 2024 revenue grew <Stat>three times</Stat> year-over-year, reaching profitability in <Stat>Q4 2024</Stat>. The platform was used across more than 65 residential complexes in 2024, up sharply from fewer than 10 at its 2022 launch.</p>
              <p className={P}>The number of properties served is expected to triple again in 2025, with the pivot to an AI platform built on advances in large language models driving the growth (funding and headcount undisclosed).</p>
            </div>
            <SourceShot
              src="/images/news-sources/hongkong-scmp.jpg"
              alt="Screenshot of the South China Morning Post article on Hong Kong startup Butler turning a profit after a tech pivot"
              media="South China Morning Post"
              url="https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Hong Kong's real estate industry has been in a privileged position for a long time. Now things are finally not going well, and they must change." cite="Angues Chan / co-founder, Butler" />
            </div>

            <SectionHeading id="s-service" en="Service">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-market" en="Market">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-pivot" en="The Pivot">The Pivot Born of a Property Downturn</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Behind the growth lies the prolonged downturn in Hong Kong's property market. With home prices continuing to fall, property managers face pressure to cut costs, fueling demand for AI tools that automate workflows.</p>
              <p className={P}>Co-founder Angues Chan stresses the need for the industry to change. The pivot from housekeeping to proptech is a prime example of turning the traditional "butler" concept into a scalable business model.</p>
            </div>
            <SourceShot
              src="/images/news-sources/hongkong-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page describing the source publication, South China Morning Post"
              media="Wikipedia (South China Morning Post)"
              url="https://en.wikipedia.org/wiki/South_China_Morning_Post"
            />

            <SectionHeading id="s-concept" en="The Concept">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Profitability</dt><dd className="text-[#333] mt-0.5">Q4 2024</dd></div>
                  <div><dt className="text-[#8f7a56]">Revenue Growth</dt><dd className="text-[#333] mt-0.5">3x YoY</dd></div>
                  <div><dt className="text-[#8f7a56]">Complexes Served</dt><dd className="text-[#333] mt-0.5">65+</dd></div>
                  <div><dt className="text-[#8f7a56]">Source</dt><dd className="text-[#333] mt-0.5">South China Morning Post</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
